import apiClient from '../config/axiosInterceptors';
import { ResponseHandler } from '../config/responseHandler';
import { API_ENDPOINTS } from '../constants/api';

class TipService {
  /**
   * Cancel/delete a tip
   * @param {string} tipId - Tip ID to cancel
   * @param {boolean} isManualRemove - Whether to manually remove the tip (true) or cancel the tip (false)  
   * @returns {Promise<Object>} Cancel tip response
   */
  async cancelTip(tipId, isManualRemove) {
    try {
      const response = await apiClient.delete(`${API_ENDPOINTS.CONTENT_TIPS}/${tipId}/tips/${isManualRemove}`);
      return ResponseHandler.handleSuccess(response, 'Tip cancelled successfully');
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to cancel tip');
    }
  }

  /**
   * Create a new tip (for content or messages)
   * @param {string} id - Content or message ID
   * @param {Object} tipData - Tip data containing tip_type, amount, returnUrl
   * @param {boolean} isMessagesTip - Whether this is a message tip (true) or content tip (false)
   * @returns {Promise<Object>} Create tip response
   */
  async sendTip(id, tipData, isMessagesTip = false) {
    try {
      const endpoint = isMessagesTip 
        ? `${API_ENDPOINTS.MESSAGE_TIPS}/${id}/tips`
        : `${API_ENDPOINTS.CONTENT_TIPS}/${id}/tips`;
      
      const response = await apiClient.post(endpoint, tipData);
      return ResponseHandler.handleSuccess(response, 'Tip created successfully');
    } catch (error) {
      throw ResponseHandler.handleError(error, 'Failed to create tip');
    }
  }

  /**
   * Extend the reply deadline for a message tip
   * @param {string} tipId - Tip ID whose deadline should be extended
   * @returns {Promise<Object>} Extend deadline response
   */
  async extendTipDeadline(tipId) {
    try {
      const response = await apiClient.put(`${API_ENDPOINTS.MESSAGE_TIPS}/${tipId}/tipExtend`);
      return ResponseHandler.handleSuccess(response, 'Tip deadline extended successfully');
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to extend tip deadline');
    }
  }

  /**
   * Creator message/tips earnings for optional subscriber (Mongo user id).
   * GET /content-creators/message-tips-earnings?subscriberId=
   * @param {string} [subscriberId] - Subscriber user id; omit for aggregate
   * @returns {Promise<Object>} ResponseHandler-shaped result
   */
  async getMessageTipsEarnings(subscriberId) {
    try {
      const params = subscriberId ? { subscriberId: String(subscriberId).trim() } : {};
      const response = await apiClient.get(API_ENDPOINTS.MESSAGE_TIPS_EARNINGS, { params });
      return ResponseHandler.handleSuccess(response, 'Message tips earnings fetched');
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to fetch message tips earnings');
    }
  }

  /**
   * Fetch total pending tip amount awaiting creator reply/collection.
   * GET /messages/tips/pending-collect
   * @returns {Promise<Object>} ResponseHandler-shaped result
   */
  async getPendingCollectTips() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.MESSAGE_TIPS_PENDING_COLLECT);
      return ResponseHandler.handleSuccess(response, 'Pending collect amount fetched');
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to fetch pending collect amount');
    }
  }
}

export default new TipService();

